/**
 * The canal (lifted from lantern-water's `canal-water.js`, `reflection.js`
 * and the shared parts of `water-light.js`): one water plane with a calm,
 * stylised surface: absorption by the distance from the bank, baked caustics
 * seen through the shallows, a reflection, downstream flow lines, ripple
 * rings at fixed emitters, a foam line at the stone, pixel sun glints, and at
 * night lantern streaks. The shader has the prototype's three tiers as a
 * define: Medium draws `WATER_TIER 2` with the planar reflection (one capped
 * render of the scene from a mirrored camera into a 768 × 512 target, with an
 * oblique clip plane at the water); Off draws `WATER_TIER 0` (flat scrolling
 * water: a gradient sky, one caustic tap, the quay wall's reflected band, no
 * rings, no reflection pass), as the Rendering page says. The palette is the
 * prototype's `WATER_LOOK`, kept in the GLSL it was tuned in.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { sceneLook } from './constants.ts';
import { type Kit, coastGlsl } from './kit.ts';

type Water = NonNullable<Scene['water']>;

const L = {
  deep: [0.012, 0.05, 0.058],
  deepLift: [0.026, 0.085, 0.084],
  shallow: [0.05, 0.1, 0.07],
  bed: [0.2, 0.16, 0.085],
  causticStrength: 0.55,
  shallowWidth: 1.25,
  zenith: [0.07, 0.12, 0.16],
  horizonWarmth: [0.85, 0.74, 0.64],
  cloud: [0.26, 0.24, 0.2],
  silhouette: [0.035, 0.03, 0.04],
  silhouetteLit: [0.1, 0.075, 0.055],
  nightDeep: [0.015, 0.01, 0.029],
  nightShallow: [0.021, 0.015, 0.036],
  nightSky: [0.022, 0.016, 0.038],
  moon: [0.07, 0.08, 0.15],
  glintCap: 3.5,
  sparkle: [1.0, 0.82, 0.55],
  sparkleHdr: 14.0,
  sparkleDensity: 2.1,
  sparkleChance: 0.18,
  ring: [0.3, 0.33, 0.3],
  foam: [0.55, 0.5, 0.42],
  lampGlow: [1.0, 0.62, 0.28],
} as const;

const f = (v: number): string => (Number.isInteger(v) ? v.toFixed(1) : String(v));
const v3 = (a: readonly number[]): string => `vec3(${a.map(f).join(',')})`;

const MAX_BRIDGES = 3;
const MAX_LAMPS = 10;
const MAX_RINGS = 8;

const vertexShader = `
varying vec3 worldPosition;
varying vec4 reflectedUv;
uniform mat4 reflectionMatrix;
#include <fog_pars_vertex>
void main(){
  vec4 world=modelMatrix*vec4(position,1.0);
  worldPosition=world.xyz;
  reflectedUv=reflectionMatrix*world;
  vec4 mvPosition=viewMatrix*world;
  gl_Position=projectionMatrix*mvPosition;
  #include <fog_vertex>
}`;

const fragmentShader = (waterY: number, coast: string, terraceFront: number, terraceHeight: number): string => `
varying vec3 worldPosition;
varying vec4 reflectedUv;
uniform float time;
uniform float night;
uniform sampler2D caustics;
uniform sampler2D reflection;
uniform float reflectionEnabled;
uniform vec2 reflectionTexel;
uniform vec4 bridges[${MAX_BRIDGES}];
uniform vec4 lamps[${MAX_LAMPS}];
uniform vec4 rings[${MAX_RINGS}];
#include <fog_pars_fragment>
${coast}
const float WATER_Y=${f(waterY)};
float noiseHash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float flowNoise(vec2 p){
  vec2 cell=floor(p),f=fract(p);
  f=f*f*(3.-2.*f);
  return mix(mix(noiseHash(cell),noiseHash(cell+vec2(1.,0.)),f.x),mix(noiseHash(cell+vec2(0.,1.)),noiseHash(cell+vec2(1.,1.)),f.x),f.y);
}
vec3 reflectedColor(vec2 uv){
  vec2 spread=reflectionTexel*vec2(1.8,0.75);
  vec2 lo=spread*2.0+reflectionTexel;
  uv=clamp(uv,lo,vec2(1.0)-lo);
  vec3 c=texture2D(reflection,uv).rgb*.4;
  c+=texture2D(reflection,uv+vec2(spread.x,0.)).rgb*.2;
  c+=texture2D(reflection,uv-vec2(spread.x,0.)).rgb*.2;
  c+=texture2D(reflection,uv+vec2(0.,spread.y)).rgb*.1;
  c+=texture2D(reflection,uv-vec2(0.,spread.y)).rgb*.1;
  return c;
}
float westSkyline(float z,float setback){
  float terrace=z<${f(-terraceFront)}?${f(terraceHeight)}:0.;
  float town=1.-smoothstep(18.,26.,abs(z));
  if(setback<.5)return(terrace+.85)*town+.5;
  float lot=floor(z/3.3);
  float roof=4.6+noiseHash(vec2(lot,3.))*2.4;
  float gable=1.2*(1.-abs(fract(z/3.3)*2.-1.))*step(.45,noiseHash(vec2(lot,9.)));
  return(terrace+.75+roof+gable)*town+1.2;
}
vec4 fakeScenery(vec3 P,vec3 r){
  vec4 hit=vec4(0.);
  vec2 bank=lwCoast(P.z);
  if(r.x<-.01){
    for(int i=0;i<2;i++){
      float setback=float(i)*2.6;
      float t=(bank.x-setback-P.x)/r.x;
      float h=t*r.y;
      float top=westSkyline(P.z+t*r.z,float(i));
      float cover=1.-smoothstep(top-.25,top+.25,h);
      vec3 c=mix(${v3(L.silhouette)},${v3(L.silhouetteLit)},smoothstep(top-1.4,top-.1,h));
      hit.rgb=mix(hit.rgb,c,cover*(1.-hit.a));
      hit.a=max(hit.a,cover);
    }
  }
  if(r.z<-.01){
    for(int i=0;i<${MAX_BRIDGES};i++){
      vec4 b=bridges[i];
      if(b.w<=0.)continue;
      float face=b.x+1.75;
      if(P.z<face)continue;
      float t=(face-P.z)/r.z;
      float x=P.x+t*r.x;
      float s=clamp((x-3.35)/7.6,0.,1.);
      float inside=step(3.1,x)*step(x,11.2);
      float deck=mix(b.y,b.z,s)+.06+sin(s*3.14159)*b.w-WATER_Y;
      float h=t*r.y;
      float solid=(1.-smoothstep(deck-.05,deck+.15,h))*inside;
      float rail=(1.-smoothstep(deck+.85,deck+1.05,h))*inside*.55;
      float cover=max(solid,rail);
      vec3 c=mix(${v3(L.silhouette)},${v3(L.silhouetteLit)},smoothstep(deck-.5,deck,h)*.6);
      hit.rgb=mix(hit.rgb,c,cover*(1.-hit.a*.5));
      hit.a=max(hit.a,cover);
    }
  }
  return hit;
}
void main(){
  vec3 P=worldPosition;
  vec3 Pq=vec3(floor(P.xz*24.)/24.,0.).xzy;
  Pq.y=P.y;
  vec2 p=P.xz-vec2(0.,time*.32);
  float broad=sin(p.y*1.85+sin(p.x*.75)*.65);
  float cross=sin(p.x*3.3+p.y*1.1);
  float fine=sin(p.y*13.5+sin(p.x*2.8)*1.3);
  vec3 normal=normalize(vec3(cross*.045,1.,broad*.065+fine*.012));
  vec3 viewDirection=normalize(cameraPosition-P);
  float fresnel=.02+.98*pow(1.-max(dot(normal,viewDirection),0.),5.);
  float dayLight=1.-night;
  vec2 bank=lwCoast(P.z);
  float edge=max(min(P.x-bank.x,bank.y-P.x),0.);
  float shallow=exp(-edge/${f(L.shallowWidth)});
  vec3 deep=mix(${v3(L.deep)},${v3(L.deepLift)},broad*.3+.5);
  vec3 body=mix(deep,${v3(L.shallow)},shallow*.85);
  vec2 cuv=Pq.xz*.19+normal.xz*.35;
  float ca=texture2D(caustics,cuv+vec2(.013,.031)*time).r;
  #if WATER_TIER > 0
  float cb=texture2D(caustics,cuv*1.37+vec2(.41-.011*time,.2+.023*time)).r;
  float caustic=min(ca,cb)*1.5+ca*cb*.6;
  #else
  float caustic=ca*ca*1.3;
  #endif
  float bedVisible=shallow*.95+.05;
  body+=${v3(L.bed)}*caustic*${f(L.causticStrength)}*bedVisible*dayLight;
  body=mix(body,mix(${v3(L.nightDeep)},${v3(L.nightShallow)},shallow),night);
  vec3 r=reflect(-viewDirection,normal);
  vec3 horizon=fogColor*${v3(L.horizonWarmth)};
  vec3 sky=mix(horizon,${v3(L.zenith)},smoothstep(.15,.9,r.y));
  #if WATER_TIER > 0
  float clouds=smoothstep(.45,.85,flowNoise(P.xz*vec2(.11,.07)+vec2(0.,time*.012))*.7+flowNoise(P.xz*.31+5.)*.3);
  #else
  float clouds=smoothstep(.3,1.,sin(P.x*.31+P.z*.17)*sin(P.z*.23-time*.01));
  #endif
  sky+=${v3(L.cloud)}*clouds*.6;
  sky=mix(sky,${v3(L.nightSky)},night);
  vec3 reflected=sky;
  #if WATER_TIER == 2
  if(reflectionEnabled>.5){
    vec2 uv=reflectedUv.xy/reflectedUv.w;
    uv+=vec2(cross*.0012,broad*.0019+fine*.0003);
    float valid=smoothstep(0.,.018,min(min(uv.x,uv.y),min(1.-uv.x,1.-uv.y)));
    reflected=mix(sky,reflectedColor(uv)*vec3(.62,.74,.72),valid);
  } else
  #endif
  {
    #if WATER_TIER > 0
    vec4 scenery=fakeScenery(P,r);
    #else
    float wall=r.x<-.01?(bank.x-P.x)/r.x*r.y:99.;
    vec4 scenery=vec4(${v3(L.silhouette)},1.-smoothstep(.7,1.1,wall));
    #endif
    reflected=mix(reflected,scenery.rgb*mix(1.,.35,night),scenery.a*.85);
  }
  reflected+=${v3(L.moon)}*night*smoothstep(.25,.75,1.-r.y)*(.6+.4*broad);
  if(night>.02){
    vec3 glow=vec3(0.);
    for(int i=0;i<${MAX_LAMPS};i++){
      vec4 lamp=lamps[i];
      if(lamp.w<.5)continue;
      vec3 mirrored=vec3(lamp.x,2.*WATER_Y-lamp.y,lamp.z);
      vec3 toLamp=mirrored-cameraPosition;
      vec3 q=cameraPosition+toLamp*((cameraPosition.y-WATER_Y)/(cameraPosition.y-mirrored.y));
      float qx=clamp(q.x,bank.x+.75,bank.y-.75);
      float pulled=abs(q.x-qx);
      float fade=1./(1.+pulled*.12);
      q.x=qx;
      q.z=mix(q.z,lamp.z+.6,smoothstep(0.,1.5,pulled));
      vec3 along=normalize(vec3(toLamp.x,0.,toLamp.z));
      vec2 d=P.xz-q.xz+normal.xz*.9;
      float a=dot(d,along.xz),c=dot(d,vec2(-along.z,along.x));
      glow+=exp(-(a*a*.55+c*c*16.))*(.75+.25*sin(a*9.-time*2.))*fade;
    }
    reflected+=${v3(L.lampGlow)}*glow*night*1.6;
  }
  float reflectionWeight=clamp(.22+fresnel*.6,.22,.68);
  vec3 col=mix(body,reflected,reflectionWeight);
  #if WATER_TIER > 0
  float variation=flowNoise(p*vec2(1.3,.62));
  #else
  float variation=.5+.5*sin(p.x*1.7+sin(p.y*.9));
  #endif
  float flowPhase=p.y*7.8+sin(p.x*2.25)*1.1+variation*3.4;
  float flowLine=pow(max(0.,sin(flowPhase)),14.)*smoothstep(.25,.85,variation+sin(p.x*4.7+p.y*.63)*.35);
  float flowVisibility=1.-smoothstep(.5,1.5,fwidth(flowPhase));
  col+=vec3(.026,.036,.03)*flowLine*flowVisibility*(1.-night*.75);
  float pixel=length(fwidth(P.xz));
  float ringLight=0.;
  #if WATER_TIER > 0
  for(int i=0;i<${MAX_RINGS};i++){
    vec4 s=rings[i];
    if(s.w<=0.)continue;
    float d=length(Pq.xz-s.xy)/s.w;
    if(d>1.1)continue;
    for(int k=0;k<2;k++){
      float age=fract(time*.32+s.z+float(k)*.5);
      float radius=.12+age*.9;
      float width=max(.035,pixel*1.2/s.w);
      ringLight+=(1.-smoothstep(width*.5,width,abs(d-radius)))*(1.-age)*(1.-age);
    }
    ringLight+=(1.-smoothstep(.06,.12,d))*.5;
  }
  #endif
  col+=${v3(L.ring)}*ringLight*mix(1.,.45,night);
  float lap=.05*sin(time*1.1+Pq.z*1.7)+.03*sin(time*1.9-Pq.z*3.1);
  #if WATER_TIER > 0
  float foamNoise=flowNoise(vec2(Pq.z*3.2,time*.4)+Pq.x);
  #else
  float foamNoise=.5+.5*sin(Pq.z*5.3+time*.9)*sin(Pq.z*2.1-time*.4);
  #endif
  float foam=(1.-smoothstep(.04,.15+foamNoise*.1,edge-lap*.5))*(.55+.45*foamNoise);
  foam*=1.-smoothstep(.15,.35,pixel);
  col=mix(col,${v3(L.foam)}*mix(1.,.3,night),foam*.55);
  vec2 sp=(Pq.xz-vec2(0.,time*.2))*${f(L.sparkleDensity)};
  vec2 cell=floor(sp);
  float h0=noiseHash(cell),h1=noiseHash(cell+17.3),h2=noiseHash(cell+41.9);
  vec2 local=(fract(sp)-(.25+.5*vec2(h1,h2)))/${f(L.sparkleDensity)};
  float size=.1;
  float footprint=max(size,pixel*.9);
  float energy=(size*size)/(footprint*footprint);
  float diamond=abs(local.x)+abs(local.y);
  float shape=1.-smoothstep(footprint*.6,footprint,diamond);
  float twinkle=pow(max(0.,sin(time*(1.6+h1*2.2)+h0*40.)),6.);
  float crest=smoothstep(-.2,.9,broad*.6+cross*.5);
  #if WATER_TIER > 0
  float patchy=smoothstep(.4,.8,flowNoise(P.xz*.23+vec2(0.,time*.05)));
  #else
  float patchy=smoothstep(.2,.9,sin(P.x*.7+time*.05)*sin(P.z*.5));
  #endif
  float glint=step(1.-${f(L.sparkleChance)},h0)*shape*twinkle*energy*crest*patchy;
  glint*=1.-smoothstep(.12,.3,pixel);
  col+=${v3(L.sparkle)}*min(glint*${f(L.sparkleHdr)},${f(L.glintCap)})*dayLight*(1.-foam);
  gl_FragColor=vec4(col,1.);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

export type CanalWater = {
  readonly mesh: THREE.Mesh;
  /** Medium: tier 2 and the planar reflection; Off: tier 0, flat. */
  setTier(medium: boolean): void;
  /** Renders the reflection for this frame (Medium only); the water plane is hidden from its own reflection. */
  renderReflection(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera): void;
  setNight(night: number): void;
  /** Frees the reflection target's GPU copy (Off). */
  release(): void;
  dispose(): void;
};

const bias = new THREE.Matrix4().set(0.5, 0, 0, 0.5, 0, 0.5, 0, 0.5, 0, 0, 0.5, 0.5, 0, 0, 0, 1);

/** The canal of a scene (`water`), the coast of its terrain, in three.js axes; `halfFloat` picks the reflection target's type. */
export const createWater = (kit: Kit, water: Water, terrain: NonNullable<Scene['terrain']>, halfFloat: boolean): CanalWater => {
  const r = sceneLook.reflection;
  const target = new THREE.WebGLRenderTarget(r.width, r.height, { type: halfFloat ? THREE.HalfFloatType : THREE.UnsignedByteType, depthBuffer: true });
  const reflectionMatrix = new THREE.Matrix4();
  const upper = terrain.districts.find((d) => d.x0 < terrain.river.west);
  const material = new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        reflection: { value: null },
        reflectionMatrix: { value: new THREE.Matrix4() },
        reflectionEnabled: { value: 0 },
        reflectionTexel: { value: new THREE.Vector2(1 / r.width, 1 / r.height) },
        bridges: { value: Array.from({ length: MAX_BRIDGES }, () => new THREE.Vector4()) },
        lamps: { value: Array.from({ length: MAX_LAMPS }, () => new THREE.Vector4()) },
        rings: { value: Array.from({ length: MAX_RINGS }, () => new THREE.Vector4()) },
      },
    ]),
    fog: true,
    defines: { WATER_TIER: 0 },
    vertexShader,
    fragmentShader: fragmentShader(water.level, coastGlsl(terrain.river.west, terrain.river.east), terrain.terraceFront, upper?.h ?? 0),
    side: THREE.DoubleSide,
  });
  // The water shares the kit's time, night and caustics (as the prototype aliased its uniforms).
  const u = material.uniforms;
  u['time'] = kit.uniforms.waterTime;
  u['night'] = kit.uniforms.waterNight;
  u['caustics'] = kit.uniforms.caustics;
  if (u['reflection'] !== undefined) u['reflection'].value = target.texture;
  if (u['reflectionMatrix'] !== undefined) u['reflectionMatrix'].value = reflectionMatrix;
  const slots = <T>(name: string): T[] => (u[name]?.value as T[] | undefined) ?? [];
  water.bridges.slice(0, MAX_BRIDGES).forEach((b, i) => slots<THREE.Vector4>('bridges')[i]?.set(-b.y, b.left, b.right, b.rise));
  water.lamps.slice(0, MAX_LAMPS).forEach((l, i) => slots<THREE.Vector4>('lamps')[i]?.set(l.x, l.h, -l.y, 1));
  water.ripples.slice(0, MAX_RINGS).forEach((s, i) => slots<THREE.Vector4>('rings')[i]?.set(s.x, -s.y, s.phase, s.scale));
  const a = water.area;
  const geometry = new THREE.PlaneGeometry(1, 1);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'canal-water';
  mesh.position.set((a.x0 + a.x1) / 2, water.level, -(a.y0 + a.y1) / 2);
  mesh.scale.set(a.x1 - a.x0, a.y1 - a.y0, 1);
  mesh.rotation.x = -Math.PI / 2;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // The plane spans the valley: drawn after the opaques, so early depth rejection skips its hidden part.
  mesh.renderOrder = 10;
  const mirror = new THREE.PerspectiveCamera();
  const clip = new THREE.Plane(new THREE.Vector3(0, 1, 0), r.clipLift);
  const q = new THREE.Vector4();
  const c = new THREE.Vector4();
  const direction = new THREE.Vector3();
  const look = new THREE.Vector3();
  let reflecting = false;
  return {
    mesh,
    setTier(medium) {
      reflecting = medium && water.reflection.medium;
      const tier = medium ? 2 : 0;
      if (material.defines['WATER_TIER'] !== tier) {
        material.defines['WATER_TIER'] = tier;
        material.needsUpdate = true;
      }
      if (u['reflectionEnabled'] !== undefined) u['reflectionEnabled'].value = reflecting ? 1 : 0;
    },
    renderReflection(renderer, scene, camera) {
      if (!reflecting) return;
      mirror.copy(camera, false);
      mirror.position.y = 2 * water.level - camera.position.y;
      direction.set(0, 0, -1).applyQuaternion(camera.quaternion);
      direction.y *= -1;
      mirror.up.copy(camera.up);
      mirror.up.y *= -1;
      look.copy(mirror.position).add(direction);
      mirror.lookAt(look);
      mirror.updateMatrixWorld(true);
      reflectionMatrix.copy(bias).multiply(mirror.projectionMatrix).multiply(mirror.matrixWorldInverse);
      const plane = clip.clone().applyMatrix4(mirror.matrixWorldInverse);
      c.set(plane.normal.x, plane.normal.y, plane.normal.z, plane.constant);
      q.set(Math.sign(c.x) || 1, Math.sign(c.y) || 1, 1, 1).applyMatrix4(mirror.projectionMatrixInverse);
      c.multiplyScalar(2 / c.dot(q));
      const p = mirror.projectionMatrix.elements;
      p[2] = c.x - (p[3] ?? 0);
      p[6] = c.y - (p[7] ?? 0);
      p[10] = c.z - (p[11] ?? 0);
      p[14] = c.w - (p[15] ?? 0);
      mirror.projectionMatrixInverse.copy(mirror.projectionMatrix).invert();
      const previous = renderer.getRenderTarget();
      const shadows = renderer.shadowMap.needsUpdate;
      mesh.visible = false;
      try {
        renderer.shadowMap.needsUpdate = false;
        renderer.setRenderTarget(target);
        renderer.render(scene, mirror);
      } finally {
        renderer.setRenderTarget(previous);
        renderer.shadowMap.needsUpdate = shadows;
        mesh.visible = true;
      }
    },
    setNight(night) {
      kit.uniforms.waterNight.value = night;
    },
    release() {
      target.dispose();
    },
    dispose() {
      mesh.removeFromParent();
      target.dispose();
      material.dispose();
      geometry.dispose();
    },
  };
};
